import { useMemo } from 'react';
import { css } from '@emotion/react';
import { PaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, Heading } from '@frontend/design-system';
import { LocationAccordionList } from '../../../../../components/location-accordion-list';
import { MAX_CALENDAR_LOCATION_COUNT } from '../../../../../constants';
import { useScheduleActionsContainerMethod } from '../../../../../context/ScheduleActionsContainerContext';
import { useSchedulingLocationInfo } from '../../../../../hooks';
import {
  useGetCalendarV2QueriesData,
  useGetFilteredAppointmentsV2,
} from '../../../../../hooks/use-get-calendar-v2-queries-data';
import { CalendarHeader } from '../../../../CalendarV3/components/header';
import { PatientListRow } from '../../../components/PatientListRow';
import { ScheduleMassMessageTrackingIds } from './trackingIds';

export const ScheduleMassMessagesV2 = () => {
  const { getScopeName } = useSchedulingLocationInfo();
  const {
    calendarLocationIds,
    filteredAppointments,
    calendarCounts,
    calendarTodayDate,
    isLoading: isLoadingAppointments,
  } = useGetCalendarV2QueriesData();

  const locationInfoList = useMemo(() => {
    return calendarLocationIds.map((locationId) => ({
      locationId,
      name: getScopeName(locationId),
      count: filteredAppointments.filter((appt) => appt.locationId === locationId).length,
    }));
  }, [filteredAppointments, getScopeName, calendarLocationIds]);

  return (
    <div css={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div css={{ zIndex: 1, boxShadow: theme.shadows.light }}>
        <CalendarHeader
          calendarTodayDate={calendarTodayDate}
          calendarEventsCount={calendarCounts.total}
          unconfirmedCalendarEventsCount={calendarCounts.unconfirmed}
          insuranceAlertCalendarEventsCount={calendarCounts.insurance}
        />
      </div>
      <div css={{ position: 'relative', flex: 1, overflowY: 'auto' }}>
        <ContentLoader size='medium' show={isLoadingAppointments} />
        <LocationAccordionList
          ContentComponent={SingleLocationView}
          locationInfoList={locationInfoList}
          style={css({ padding: theme.spacing(2) })}
        />
      </div>
    </div>
  );
};

interface SingleLocationViewProps {
  locationId: string;
  height?: number | string;
}

const SingleLocationView = ({ locationId, height }: SingleLocationViewProps) => {
  const { t } = useTranslation('schedule-pulse');
  const { setHideTabs, setSelectedLocationId } = useScheduleActionsContainerMethod();
  const { filteredAppointments, calendarLocationIds } = useGetCalendarV2QueriesData();

  const isMoreThanMaxLocation = calendarLocationIds.length > MAX_CALENDAR_LOCATION_COUNT;
  const { filteredAppointments: filteredAppointmentsByLocation, isLoading } = useGetFilteredAppointmentsV2(
    isMoreThanMaxLocation ? [locationId] : []
  );

  const appointments = useMemo(
    () =>
      isMoreThanMaxLocation
        ? filteredAppointmentsByLocation
        : (filteredAppointments ?? []).filter((appt) => appt.locationId === locationId),
    [filteredAppointments, locationId, isMoreThanMaxLocation, filteredAppointmentsByLocation]
  );

  return (
    <>
      <ContentLoader size='medium' show={isLoading} />
      <div css={locationTopSectionStyle}>
        <Heading level={3} css={{ fontSize: theme.font.size.large }}>
          {appointments.length ? t('{{count}} Patients', { count: appointments.length }) : t('No Appointments')}
        </Heading>
        <Button
          variant='tertiary'
          trackingId={ScheduleMassMessageTrackingIds.contactPatientsBtn}
          disabled={!appointments.length}
          onClick={() => {
            setSelectedLocationId(locationId);
            setHideTabs(true);
          }}
        >
          {t('Contact Patients')}
        </Button>
      </div>

      <div css={{ background: theme.colors.white, flex: 1 }}>
        <PaginatedList
          emptyStateConfig={{
            header: t('No Appointments found'),
            type: 'schedule',
          }}
          height={typeof height === 'number' ? height - 65 : height ?? '100%'} // 65px is the height of the top section
          listData={appointments}
          renderListItem={({ listItem }) => (
            <>
              <PatientListRow
                appointments={appointments}
                appt={listItem}
                shouldShowAppointmentModal
                locationId={locationId}
                shouldShowActionMenu
              />
              <hr css={{ border: 'none', borderBottom: `1px solid ${theme.colors.neutral20}` }} />
            </>
          )}
          endOfListText=''
        />
      </div>
    </>
  );
};

const locationTopSectionStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.colors.white,
  borderBottom: `1px solid ${theme.colors.neutral20}`,
});
